import { useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Building2, Trash2, Upload, UserCircle2 } from 'lucide-react';
import { cropToSquareDataUrl, MAX_SOURCE_BYTES, readFileAsDataUrl } from '../../lib/imageCrop';
import { IMAGE_FIELD_COPY, type ImageFieldKind } from '../../lib/imageFieldCopy';

interface Props {
  /** Current logo as a data URL, or '' when the company has none. */
  value: string;
  onChange: (logoUrl: string) => void;
  disabled?: boolean;
  /** Same picker + crop for a person's profile photo (round, its own copy). */
  kind?: ImageFieldKind;
}

/**
 * Company logo picker with a square crop step.
 *
 * The chosen file never leaves the browser as-is: it is cropped to a square and
 * re-encoded small enough to live in the company row itself (see lib/imageCrop),
 * which is why there is no upload endpoint here — the logo is saved with the
 * rest of the form.
 */
export function CompanyLogoField({ value, onChange, disabled, kind = 'logo' }: Props) {
  const copy = IMAGE_FIELD_COPY[kind];
  const inputRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | undefined) {
    setError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Alege un fișier imagine (PNG, JPG, SVG…).');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError('Fișierul este prea mare (maximum 8 MB).');
      return;
    }
    try {
      setSource(await readFileAsDataUrl(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fișierul nu a putut fi citit.');
    }
  }

  return (
    <div>
      <span className="block text-sm font-medium text-gray-700">{copy.label}</span>

      <div className="mt-2 flex items-center gap-4">
        {value ? (
          <img
            src={value}
            alt={copy.alt}
            className={`h-16 w-16 shrink-0 border border-gray-200 object-cover ${copy.shape}`}
          />
        ) : (
          <div
            className={`flex h-16 w-16 shrink-0 items-center justify-center border border-dashed border-gray-300 text-gray-300 ${copy.shape}`}
          >
            {kind === 'avatar' ? <UserCircle2 size={26} /> : <Building2 size={22} />}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="ui-btn-secondary inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Upload size={14} />
            {value ? copy.change : copy.upload}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} />
              Elimină
            </button>
          )}
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-400">{copy.hint}</p>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          void handleFile(e.target.files?.[0]);
          // Reset so picking the same file twice still fires onChange.
          e.target.value = '';
        }}
      />

      {source && (
        <LogoCropDialog
          source={source}
          title={copy.cropTitle}
          useLabel={copy.use}
          onCancel={() => setSource(null)}
          onDone={(dataUrl) => {
            onChange(dataUrl);
            setSource(null);
          }}
        />
      )}
    </div>
  );
}

/** Modal wrapping react-easy-crop with a fixed 1:1 viewport and a zoom slider. */
function LogoCropDialog({
  source,
  title,
  useLabel,
  onCancel,
  onDone,
}: {
  source: string;
  title: string;
  useLabel: string;
  onCancel: () => void;
  onDone: (dataUrl: string) => void;
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  async function handleSave() {
    if (!area) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await cropToSquareDataUrl(source, area));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Decuparea a eșuat.');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-600">
          Trage imaginea pentru a o poziționa și folosește slider-ul pentru zoom.
        </p>

        <div className="relative mt-4 h-64 overflow-hidden rounded-md bg-gray-900">
          <Cropper
            image={source}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_area, pixels) => setArea(pixels)}
          />
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          Zoom
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="mt-1 w-full"
          />
        </label>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="ui-btn-secondary disabled:opacity-50"
          >
            Anulează
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !area}
            className="ui-btn-primary disabled:opacity-50"
          >
            {busy ? 'Se procesează…' : useLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
